import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";

interface propsType {
  userState: userState;
  reservationUser?: userState;
  adminMode?: boolean;
  hideReservationButton?: boolean;
  reloadKey?: number;
  onReservationChange?: () => void;
}

interface RobotCleanerReservation {
  reservationId: string;
  reservDate: string;
  reservDt: string;
  userId: string;
  userName: string;
  userAddr: string;
  reservState: string;
  returnDueDate?: string;
  useEndTime?: string;
  deviceId?: string;
}

const mainApi = new HttpMainApi();

function formatDateLabel(date: string) {
  return `${date.slice(0, 4)}년 ${date.slice(4, 6)}월 ${date.slice(6, 8)}일`;
}

function formatReturnDateLabel(reservDate: string, returnDueDate?: string, useEndTime?: string) {
  if (returnDueDate) return `${returnDueDate} ${useEndTime || "17:00"}`;
  const returnDate = new Date(
    Number(reservDate.slice(0, 4)),
    Number(reservDate.slice(4, 6)) - 1,
    Number(reservDate.slice(6, 8)) + 6,
  );
  const formattedDate = `${returnDate.getFullYear()}년 ${String(returnDate.getMonth() + 1).padStart(2, "0")}월 ${String(returnDate.getDate()).padStart(2, "0")}일`;
  return `${formattedDate} 17:00`;
}

async function getMyRobotCleanerReservations(userId: string, fromDate: string) {
  const res = await mainApi.get_my_robot_cleaner_reservation({
    userId,
    fromDate,
  });
  if (res.code === "200") {
    return res.response.result;
  }
  return [];
}

function sortByNewestReservationDate(reservations: RobotCleanerReservation[]) {
  return [...reservations].sort((a, b) => b.reservDate.localeCompare(a.reservDate));
}

function canCancelReservation(reservDate: string) {
  const nowInfo = getFormattedDateTime();
  const today = nowInfo.formatpk;
  const currentTime = nowInfo.formathhmmss.slice(0, 4);

  if (reservDate < today) return false;
  if (reservDate > today) return true;
  return currentTime < "0900";
}

function getCancelUnavailableMessage(reservDate: string) {
  const today = getFormattedDateTime().formatpk;
  if (reservDate < today) return "지난 예약은 취소할 수 없습니다.";
  return "예약 당일 09시 이후에는 취소할 수 없습니다.";
}

const ReservationRobotCleanerEnd = (props: propsType) => {
  const navigate = useNavigate();
  const reservationUser = props.reservationUser || props.userState;
  const fromDate = "00000000";
  const [reservationData, setReservationData] = useState<RobotCleanerReservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadMyReservations = async () => {
    setIsLoading(true);
    try {
      const reservations = await getMyRobotCleanerReservations(reservationUser.userId, fromDate);
      setReservationData(sortByNewestReservationDate(reservations));
    } catch (error) {
      alert(`예약 내역 조회 중 오류가 발생했습니다.\n${error}`);
      setReservationData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    const loadInitialReservations = async () => {
      setIsLoading(true);
      try {
        const reservations = await getMyRobotCleanerReservations(reservationUser.userId, fromDate);
        if (isActive) {
          setReservationData(sortByNewestReservationDate(reservations));
        }
      } catch (error) {
        if (isActive) {
          alert(`예약 내역 조회 중 오류가 발생했습니다.\n${error}`);
          setReservationData([]);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadInitialReservations();
    return () => {
      isActive = false;
    };
  }, [reservationUser.userId, fromDate, props.reloadKey]);

  const reservationCancel = async (reservation: RobotCleanerReservation) => {
    if (!window.confirm("예약을 취소하시겠습니까?")) return;

    try {
      const res = await mainApi.put_cancel_robot_cleaner_reservation({
        reservDate: reservation.reservDate,
        ...(reservation.reservationId ? { reservationId: reservation.reservationId } : {}),
        userId: reservationUser.userId,
        inputDt: getFormattedDateTime().compactDateTime,
        ...(props.adminMode ? { adminMode: true } : {}),
      });
      if (res.code === "200") {
        alert("예약이 취소되었습니다.");
        await loadMyReservations();
        if (props.onReservationChange) {
          props.onReservationChange();
        }
      } else if (res.code === "602") {
        alert("예약 당일 09시 이후에는 취소할 수 없습니다.");
        await loadMyReservations();
      } else {
        alert(`예약 취소 중 오류가 발생했습니다.\n- CODE : ${res.code}`);
      }
    } catch (error) {
      alert(`예약 취소 중 오류가 발생했습니다.\n${error}`);
    }
  };

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: "40rem",
        margin: "0 auto",
        padding: "16px",
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          창문 로봇 청소기 예약 내역
        </Typography>
        {!props.hideReservationButton ? (
          <Button onClick={() => navigate("/reservationrobotcleaner")}>예약하기</Button>
        ) : null}
      </Stack>

      <Alert severity="info">
        {props.adminMode
          ? "관리자는 날짜 제한 없이 대리 취소할 수 있습니다."
          : "예약 당일 오전 9시 전까지만 취소할 수 있습니다."}
      </Alert>

      {isLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">예약 내역을 불러오는 중입니다.</Typography>
        </Stack>
      ) : null}

      {!isLoading && reservationData.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography color="text.secondary">예약 내역이 없습니다.</Typography>
        </Box>
      ) : null}

      {reservationData.map((reservation) => {
        const isCancelable = props.adminMode || canCancelReservation(reservation.reservDate);
        return (
          <Box
            key={reservation.reservationId || reservation.reservDate}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#fff",
            }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                {reservation.reservDt || formatDateLabel(reservation.reservDate)}
              </Typography>
              <Typography variant="body2" fontWeight={800} sx={{ color: "#1976d2" }}>
                반납 일자 : {formatReturnDateLabel(reservation.reservDate, reservation.returnDueDate, reservation.useEndTime)}
              </Typography>
              <Typography variant="body2">예약자 : {reservation.userName}</Typography>
              <Divider sx={{ my: 1 }} />
              <Button
                variant="outlined"
                color="error"
                disabled={!isCancelable}
                onClick={() => reservationCancel(reservation)}
              >
                예약 취소
              </Button>
              {!isCancelable ? (
                <Typography variant="caption" color="text.secondary">
                  {getCancelUnavailableMessage(reservation.reservDate)}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default ReservationRobotCleanerEnd;
